import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock3 } from 'lucide-react';
import { relativeFromMicros } from '@/features/notifications/lib/notificationTime';
import { recentHref } from '../lib/recentHref';
import { useRecentStore } from '../store/recentStore';

/**
 * The module panel's "Recent" section (M4 · 86d44z223).
 *
 * Renders NOTHING — heading included — when this module has nothing to show. A "Recent" heading over
 * empty space is the same failure as an orphaned "Quick actions" heading.
 *
 * There is deliberately no skeleton: this sits below the nav tree the user came for, and a shimmer
 * appearing under it on every panel open would draw the eye to the least important thing on screen.
 * It simply appears once loaded.
 */
export function RecentSection({ moduleId }: { moduleId: string }) {
  const rows = useRecentStore((s) => s.byModule[moduleId]);
  const fetchForModule = useRecentStore((s) => s.fetchForModule);

  // Fetch on panel open. The panel is keyed by module and unmounts when closed, so this is exactly
  // once per open — no polling, no subscription.
  useEffect(() => {
    void fetchForModule(moduleId);
  }, [moduleId, fetchForModule]);

  // Drop rows we cannot address. `recentHref` returns null for a kind this client does not know,
  // and a row that cannot navigate is worse than a row that is absent.
  const navigable = (rows ?? [])
    .map((r) => ({ row: r, href: recentHref(r.entityType, r.entityId) }))
    .filter((x): x is { row: (typeof rows)[number]; href: string } => x.href !== null);

  if (navigable.length === 0) return null;

  const nowMs = Date.now();

  return (
    <>
      <p className="m-0 px-2.5 pb-1.5 pt-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Recent
      </p>
      <div className="flex flex-col gap-0.5">
        {navigable.map(({ row, href }) => (
          <Link
            key={`${row.entityType}|${row.entityId}`}
            to={href}
            className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sidebar-foreground transition-colors hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          >
            <Clock3 className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-sm">{row.label}</span>
            {/* tabular-nums so the timestamps form a column instead of shimmering as they tick. */}
            <span className="flex-shrink-0 text-xs tabular-nums text-muted-foreground">
              {/* The shell's house voice for elapsed time, reused rather than re-derived. It takes
                  MICROseconds; this wire carries ISO-8601. */}
              {relativeFromMicros(Date.parse(row.viewedAtUtc) * 1000, nowMs)}
            </span>
          </Link>
        ))}
      </div>
    </>
  );
}
